import React, { useContext } from "react";
import { useSelector } from "react-redux";
import { Check, Dashboard, Undo } from "@mui/icons-material";
import { Divider, ListItemIcon, MenuItem } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Actions } from "flexlayout-react";
import { ITabAttributes } from "flexlayout-react/declarations/model/IJsonModel";

import { multiProjectWidgets, projectWidgets } from "fond/layout/defaultLayout";
import { LayoutContext } from "fond/layout/LayoutProvider";
import { getCurrentProject } from "fond/project";
import { Store, Widget } from "fond/types";
import { NestedMenuItem } from "fond/widgets";

import { trackPageMenuItemClick } from "../helper";

const useStyles = makeStyles(() => ({
  menuList: {
    padding: 0,
  },
}));

interface IProps {
  type: "project" | "multiProject";
  parentMenuOpen: boolean;
  handleClose: () => void;
}

const LayoutMenu: React.FC<IProps> = ({ type, parentMenuOpen, handleClose }: IProps) => {
  const classes = useStyles();
  const layout = useContext(LayoutContext);
  const project = useSelector((state: Store) => getCurrentProject(state.project));

  const widgets = type === "project" ? projectWidgets : multiProjectWidgets;

  /**
   * Handles adding or removing a widget from the layout
   */
  const handleOnClick = (widget: ITabAttributes) => {
    if (layout?.model && widget.id) {
      const node = layout.model.getNodeById(widget.id);
      if (node) {
        layout.model.doAction(Actions.deleteTab(widget.id));
      } else {
        // Toggle maximize if there is any maximized widget before adding a new one
        const maximizedWidget = layout.model.getMaximizedTabset();
        if (maximizedWidget) {
          layout.model.doAction(Actions.maximizeToggle(maximizedWidget.getId()));
        }
        layout?.layoutRef?.current?.addTabWithDragAndDropIndirect(`Drag & Drop: ${widget.name}`, widget);
      }
    }

    handleClose();
    trackPageMenuItemClick("view", `layout-${widget.id}`, project);
  };

  const isWidgetOnLayout = (id: Widget): boolean => {
    if (layout?.model) {
      const node = layout.model.getNodeById(id);
      return Boolean(node);
    }
    return false;
  };

  return (
    <NestedMenuItem
      data-testid="project-menu-layout-widgets"
      label="Layout"
      parentMenuOpen={parentMenuOpen}
      leftIcon={
        <ListItemIcon>
          <Dashboard />
        </ListItemIcon>
      }
      MenuProps={{
        open: true,
        MenuListProps: { dense: true },
      }}
      menuClasses={{ list: classes.menuList }}
    >
      {Object.values(widgets).map((widget) => (
        <MenuItem
          key={`map-style-${widget.id}`}
          data-testid={`map-style-${widget.id}`}
          onClick={() => {
            handleOnClick(widget);
          }}
          disabled={widget.id === Widget.Map || widget.id === Widget.CityMap}
        >
          {widget.id === Widget.Map || widget.id === Widget.CityMap ? (
            <ListItemIcon>
              <Check />
            </ListItemIcon>
          ) : (
            <ListItemIcon>{isWidgetOnLayout(widget.id) && <Check />}</ListItemIcon>
          )}

          {widget.name}
        </MenuItem>
      ))}
      <Divider style={{ margin: 0 }} />
      <MenuItem data-testid="map-style-default" onClick={layout?.reset}>
        <ListItemIcon>
          <Undo />
        </ListItemIcon>
        Default Layout
      </MenuItem>
    </NestedMenuItem>
  );
};

export default LayoutMenu;
